import { useState, useEffect, useCallback } from "react";

const DOCKER_SERVERS = {
  micro:       { label:"TN-Micro",     url:"/micro-api",          accent:"#10b981", env:"tnpdcl" },
  tnpdcldock2: { label:"TN-Docker-2", url:"/tnpdcl-docker2-api", accent:"#ec4899", env:"tnpdcl" },
  fsadocker1: { label:"BL-PROD-FSA-Docker-1", url:"/fsa-docker1-api", accent:"#f59e0b", env:"fsa" },
};

function parseFloat2(v) {
  if (!v || v==="N/A") return 0;
  return parseFloat(v.replace("%","")) || 0;
}

function statusColor(s) {
  if (s==="running") return "#10b981";
  if (s==="failed")  return "#ef4444";
  if (s==="stopped") return "#64748b";
  return "#f59e0b";
}

export default function DockerPanel({ token, theme, env }) {
  const _curEnv = env || (typeof sessionStorage!=="undefined"?sessionStorage.getItem("bn360_env"):null) || "tnpdcl";
  const visibleServers = Object.fromEntries(
    Object.entries(DOCKER_SERVERS).filter(([k,v]) =>
      _curEnv==="fsa" ? v.env==="fsa" : v.env==="tnpdcl"
    )
  );
  const [server,   setServer]   = useState(sessionStorage.getItem("bn360_env")==="fsa"?"fsadocker1":"micro");
  useEffect(()=>{setServer(sessionStorage.getItem("bn360_env")==="fsa"?"fsadocker1":"micro");},[env]);
  const [data,     setData]     = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [search,   setSearch]   = useState("");
  const [filter,   setFilter]   = useState("all");
  const [selCont,  setSelCont]  = useState(null);
  const [logs,     setLogs]     = useState(null);
  const [logsLoading, setLogsLoading] = useState(false);
  const [sortBy,   setSortBy]   = useState("cpu");
  const [showImages, setShowImages] = useState(false);
  const [images, setImages] = useState([]);
  const [imgLoading, setImgLoading] = useState(false);
  const [showDockerLogs, setShowDockerLogs] = useState(false);
  const [dockerLogs, setDockerLogs] = useState([]);
  const [dockerHistory, setDockerHistory] = useState([]);
  const [showDockerHistory, setShowDockerHistory] = useState(false);
  const [errOnly,  setErrOnly]  = useState(false);
  const [restarting, setRestarting] = useState(false);

  const H   = { "X-Auth-Token": token };
  const loadDockerLogs = async () => {
    setLogsLoading(true);
    try {
      const r = await fetch(`${cfg.url}/docker/logs`, { headers: H });
      const d = await r.json();
      setDockerLogs(d.logs || []);
    } catch(e) {}
    setLogsLoading(false);
  };
  const truncateDockerLog = async (name) => {
    if (!window.confirm(`Truncate logs for ${name}?`)) return;
    try { await fetch(`${cfg.url}/docker/logs/${name}/truncate`,{method:"POST",headers:H}); loadDockerLogs(); } catch(e) {}
  };
  const loadImages=async()=>{setImgLoading(true);try{const r=await fetch(`${cfg.url}/docker/images`,{headers:H});const d=await r.json();setImages(d.images||[]);}catch(e){}setImgLoading(false);};
  const deleteImage=async(img)=>{if(!window.confirm(`Delete ${img.repo}?`))return;try{await fetch(`${cfg.url}/docker/images/${img.id}/delete`,{method:"POST",headers:H});loadImages();}catch(e){}};
  const pruneImages=async()=>{if(!window.confirm("Prune unused?"))return;try{await fetch(`${cfg.url}/docker/prune`,{method:"POST",headers:H});loadImages();}catch(e){}};
  const loadDockerHistory=async()=>{try{console.log("Fetching history from:",cfg.url);const r=await fetch(`${cfg.url}/docker/watchdog-history`,{headers:H});const d=await r.json();console.log("History data:",d);setDockerHistory(d.history||[]);}catch(e){console.error("Docker history error:",e);}};
  const cfg = DOCKER_SERVERS[server] || Object.values(visibleServers)[0] || DOCKER_SERVERS.micro;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${cfg.url}/services`, { headers: H });
      setData(await r.json());
    } catch(e) { console.error(e); }
    setLoading(false);
  }, [server, token]);

  useEffect(() => {
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, [load]);

  const loadLogs = async (name) => {
    setSelCont(name); setLogsLoading(true); setLogs(null);
    try {
      const r = await fetch(`${cfg.url}/services/${name}`, { headers: H });
      const d = await r.json();
      setLogs(d.logs || []);
    } catch(e) { console.error(e); }
    setLogsLoading(false);
  };

  const restartContainer = async (name) => {
    if (!window.confirm(`Restart ${name}?`)) return;
    setRestarting(true);
    try {
      await fetch(`${cfg.url}/services/${name}/restart`, { method:"POST", headers: H });
      setTimeout(()=>{ load(); setRestarting(false); }, 3000);
    } catch(e) { setRestarting(false); }
  };

  const card = { background:theme.bgCard, border:`1px solid ${theme.border}`, borderRadius:12, padding:"16px 20px" };

  const containers = (data?.services || [])
    .filter(c => filter==="all" || c.status===filter)
    .filter(c => !search || c.name.toLowerCase().includes(search.toLowerCase()) || c.description?.toLowerCase().includes(search.toLowerCase()))
    .sort((a,b) => {
      if (sortBy==="cpu")    return parseFloat2(b.cpu) - parseFloat2(a.cpu);
      if (sortBy==="mem")    return parseFloat2(b.mem_pct) - parseFloat2(a.mem_pct);
      if (sortBy==="name")   return a.name.localeCompare(b.name);
      if (sortBy==="status") return a.status.localeCompare(b.status);
      return 0;
    });

  const summary = data?.summary || {};

  return (
    <div style={{flex:1,display:"flex",flexDirection:"column",
      background:theme.bg,fontFamily:"'Inter',sans-serif",overflow:"hidden",minHeight:0,height:"100%"}}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>

      {/* Header */}
      <div style={{padding:"12px 20px",borderBottom:`1px solid ${theme.border}`,
        background:theme.bgCard,display:"flex",alignItems:"center",gap:12,flexWrap:"wrap",flexShrink:0}}>
        <div style={{fontSize:16,fontWeight:800,color:theme.text}}>🐳 Docker Monitor</div>
        <div style={{width:1,height:20,background:theme.border}}/>
        {Object.entries(visibleServers).map(([k,s])=>(
          <button key={k} onClick={()=>setServer(k)} style={{
            background:server===k?`${s.accent}18`:theme.inputBg,
            border:`1px solid ${server===k?`${s.accent}50`:theme.border}`,
            color:server===k?s.accent:theme.textMuted,
            borderRadius:6,padding:"5px 12px",cursor:"pointer",
            fontFamily:"'Inter',sans-serif",fontSize:12,fontWeight:600,
          }}>{s.label}</button>
        ))}
        <div style={{width:1,height:20,background:theme.border}}/>
        {/* Filter buttons */}
        {[["all","All"],["running","Running"],["stopped","Stopped"],["failed","Failed"]].map(([k,l])=>(
          <button key={k} onClick={()=>setFilter(k)} style={{
            background:filter===k?`${cfg.accent}18`:theme.inputBg,
            border:`1px solid ${filter===k?`${cfg.accent}50`:theme.border}`,
            color:filter===k?cfg.accent:theme.textMuted,
            borderRadius:6,padding:"5px 10px",cursor:"pointer",
            fontFamily:"'Inter',sans-serif",fontSize:12,fontWeight:600,
          }}>{l}</button>
        ))}
        <input value={search} onChange={e=>setSearch(e.target.value)}
          placeholder="🔍 Search containers..."
          style={{background:theme.inputBg,border:`1px solid ${theme.border}`,
            color:theme.text,borderRadius:6,padding:"5px 12px",
            fontFamily:"'Inter',sans-serif",fontSize:12,outline:"none",width:180}}/>
        <div style={{flex:1}}/>
        <button onClick={load} style={{
          background:`${cfg.accent}18`,border:`1px solid ${cfg.accent}50`,
          color:cfg.accent,borderRadius:6,padding:"5px 12px",cursor:"pointer",
          fontFamily:"'Inter',sans-serif",fontSize:12,fontWeight:600,
        }}>↻ Refresh</button>
          <button onClick={()=>{setShowDockerLogs(v=>{if(!v)loadDockerLogs();return !v;});}}
          style={{fontSize:11,padding:"4px 12px",borderRadius:6,cursor:"pointer",
            background:showDockerLogs?"rgba(245,158,11,0.15)":theme.inputBg,
            border:`1px solid ${showDockerLogs?"#f59e0b":theme.border}`,
            color:showDockerLogs?"#f59e0b":theme.textMuted,
            fontFamily:"'Inter',sans-serif",fontWeight:600}}>
          📋 Log Sizes
        </button>
        <button onClick={()=>{setShowDockerHistory(v=>{if(!v)loadDockerHistory();return !v;});}}
          style={{fontSize:11,padding:"4px 12px",borderRadius:6,cursor:"pointer",
            background:showDockerHistory?"rgba(239,68,68,0.15)":theme.inputBg,
            border:`1px solid ${showDockerHistory?"#ef4444":theme.border}`,
            color:showDockerHistory?"#ef4444":theme.textMuted,
            fontFamily:"'Inter',sans-serif",fontWeight:600}}>
          🔄 Restart History
        </button>
        <button onClick={()=>{setShowImages(v=>!v);if(!showImages)loadImages();}} style={{fontSize:11,padding:"4px 12px",borderRadius:6,cursor:"pointer",background:showImages?"rgba(6,182,212,0.15)":theme.inputBg,border:"1px solid #06b6d4",color:"#06b6d4",fontFamily:"Inter,sans-serif",fontWeight:600}}>🖼 Images</button>
        {loading&&<div style={{width:16,height:16,borderRadius:"50%",
          border:`2px solid ${cfg.accent}30`,borderTopColor:cfg.accent,
          animation:"spin .7s linear infinite"}}/>}
      </div>

      <div style={{flex:1,display:"flex",overflow:"hidden",minHeight:0}}>
        {/* Left: container list */}
        <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden",minHeight:0,height:"100%"}}>

          {/* Summary + sort */}
          <div style={{padding:"12px 20px",borderBottom:`1px solid ${theme.border}`,
            display:"flex",alignItems:"center",gap:12,flexShrink:0,flexWrap:"wrap"}}>
            {[
              ["TOTAL", summary.total||0, theme.text],
              ["RUNNING", summary.running||0, "#10b981"],
              ["STOPPED", summary.stopped||0, "#64748b"],
              ["FAILED", summary.failed||0, "#ef4444"],
            ].map(([l,v,c])=>(
              <div key={l} style={{display:"flex",alignItems:"center",gap:6}}>
                <span style={{fontSize:11,color:theme.textMuted,fontWeight:700,letterSpacing:".08em"}}>{l}</span>
                <span style={{fontSize:16,fontWeight:800,color:c,fontFamily:"'JetBrains Mono',monospace"}}>{v}</span>
              </div>
            ))}
            <div style={{width:1,height:16,background:theme.border}}/>
            <span style={{fontSize:11,color:theme.textMuted}}>SORT:</span>
            {[["cpu","CPU"],["mem","MEM%"],["name","Name"],["status","Status"]].map(([k,l])=>(
              <button key={k} onClick={()=>setSortBy(k)} style={{
                background:sortBy===k?`${cfg.accent}18`:theme.inputBg,
                border:`1px solid ${sortBy===k?`${cfg.accent}50`:theme.border}`,
                color:sortBy===k?cfg.accent:theme.textMuted,
                borderRadius:5,padding:"3px 10px",cursor:"pointer",
                fontFamily:"'Inter',sans-serif",fontSize:11,fontWeight:600,
              }}>{l}</button>
            ))}
            <span style={{fontSize:11,color:theme.textMuted,marginLeft:"auto"}}>
              {containers.length} containers
            </span>
          </div>

          {/* Container list */}
          <div style={{flex:"1 1 0",overflowY:"auto",minHeight:0}}>
            {/* Table header */}
            <div style={{display:"grid",
              gridTemplateColumns:"2fr 80px 70px 70px 80px 100px 120px",
              gap:8,padding:"8px 20px",fontSize:10,fontWeight:700,
              color:theme.textMuted,letterSpacing:".08em",
              borderBottom:`1px solid ${theme.border}`,
              background:theme.bgCard,position:"sticky",top:0,zIndex:1}}>
              <span>CONTAINER</span>
              <span>STATUS</span>
              <span>CPU</span>
              <span>MEM%</span>
              <span>MEMORY</span>
              <span>MEM LIMIT</span>
              <span>PORTS</span>
            </div>

            {containers.map((c,i)=>(
              <div key={c.name}
                onClick={()=>loadLogs(c.name)}
                style={{display:"grid",
                  gridTemplateColumns:"2fr 80px 70px 70px 80px 100px 120px",
                  gap:8,padding:"10px 20px",cursor:"pointer",
                  background:selCont===c.name?`${cfg.accent}08`:i%2===0?theme.inputBg+"40":"transparent",
                  borderBottom:`1px solid ${theme.border}30`,
                  borderLeft:selCont===c.name?`3px solid ${cfg.accent}`:"3px solid transparent",
                  transition:"all .1s"}}>

                {/* Name + image */}
                <div style={{display:"flex",flexDirection:"column",gap:2,overflow:"hidden"}}>
                  <div style={{display:"flex",alignItems:"center",gap:6}}>
                    <div style={{width:6,height:6,borderRadius:"50%",flexShrink:0,
                      background:statusColor(c.status)}}/>
                    <span style={{fontSize:13,fontWeight:600,color:theme.text,
                      overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{c.name}</span>
                  </div>
                  <span style={{fontSize:11,color:theme.textMuted,
                    overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",
                    paddingLeft:12}}>{c.description}</span>
                </div>

                {/* Status */}
                <div style={{display:"flex",alignItems:"center"}}>
                  <span style={{fontSize:11,fontWeight:700,padding:"2px 8px",borderRadius:5,
                    background:`${statusColor(c.status)}18`,color:statusColor(c.status)}}>
                    {c.status.toUpperCase()}
                  </span>
                </div>

                {/* CPU */}
                <div style={{display:"flex",flexDirection:"column",gap:3,justifyContent:"center"}}>
                  <span style={{fontSize:12,fontWeight:700,color:
                    parseFloat2(c.cpu)>50?"#ef4444":parseFloat2(c.cpu)>20?"#f59e0b":"#10b981",
                    fontFamily:"'JetBrains Mono',monospace"}}>{c.cpu||"—"}</span>
                  <div style={{background:theme.border,borderRadius:2,height:3}}>
                    <div style={{width:`${Math.min(parseFloat2(c.cpu)*2,100)}%`,height:"100%",
                      borderRadius:2,background:parseFloat2(c.cpu)>50?"#ef4444":cfg.accent}}/>
                  </div>
                </div>

                {/* MEM% */}
                <div style={{display:"flex",flexDirection:"column",gap:3,justifyContent:"center"}}>
                  <span style={{fontSize:12,fontWeight:700,color:
                    parseFloat2(c.mem_pct)>85?"#ef4444":parseFloat2(c.mem_pct)>60?"#f59e0b":"#06b6d4",
                    fontFamily:"'JetBrains Mono',monospace"}}>{c.mem_pct||"—"}</span>
                  <div style={{background:theme.border,borderRadius:2,height:3}}>
                    <div style={{width:`${Math.min(parseFloat2(c.mem_pct),100)}%`,height:"100%",
                      borderRadius:2,background:parseFloat2(c.mem_pct)>85?"#ef4444":"#06b6d4"}}/>
                  </div>
                </div>

                {/* Memory */}
                <span style={{fontSize:12,color:theme.text,fontFamily:"'JetBrains Mono',monospace",
                  display:"flex",alignItems:"center"}}>{c.memory||"—"}</span>

                {/* Mem limit */}
                <span style={{fontSize:12,color:theme.textMuted,fontFamily:"'JetBrains Mono',monospace",
                  display:"flex",alignItems:"center"}}>{c.mem_limit||"—"}</span>

                {/* Ports */}
                <div style={{display:"flex",flexWrap:"wrap",gap:3,alignItems:"center"}}>
                  {(c.ports||[]).slice(0,2).map((p,pi)=>(
                    <span key={pi} style={{fontSize:10,color:cfg.accent,
                      background:`${cfg.accent}15`,padding:"1px 5px",borderRadius:4,
                      fontFamily:"'JetBrains Mono',monospace"}}>{p}</span>
                  ))}
                  {(c.ports||[]).length>2 && (
                    <span style={{fontSize:10,color:theme.textMuted}}>+{c.ports.length-2}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right: logs panel */}
        {selCont && (
          <div style={{width:480,borderLeft:`1px solid ${theme.border}`,
            display:"flex",flexDirection:"column",overflow:"hidden",
            flex:"0 0 480px",minHeight:0}}>
            <div style={{padding:"12px 16px",borderBottom:`1px solid ${theme.border}`,
              background:theme.bgCard,display:"flex",alignItems:"center",justifyContent:"space-between",gap:8}}>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:13,fontWeight:700,color:theme.text,
                  overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{selCont}</div>
                <div style={{fontSize:11,color:theme.textMuted}}>Last 1000 lines</div>
              </div>
              <div style={{display:"flex",gap:6,alignItems:"center",flexShrink:0}}>
                <button onClick={()=>setErrOnly(v=>!v)} style={{
                  background:errOnly?"rgba(239,68,68,0.15)":theme.inputBg,
                  border:`1px solid ${errOnly?"#ef4444":"transparent"}`,
                  color:errOnly?"#ef4444":theme.textMuted,
                  borderRadius:5,padding:"3px 8px",cursor:"pointer",
                  fontFamily:"'Inter',sans-serif",fontSize:11,fontWeight:600,
                }}>⚠ Errors</button>
                <button onClick={()=>restartContainer(selCont)} disabled={restarting} style={{
                  background:"rgba(239,68,68,0.1)",border:"1px solid rgba(239,68,68,0.3)",
                  color:"#ef4444",borderRadius:5,padding:"3px 8px",cursor:"pointer",
                  fontFamily:"'Inter',sans-serif",fontSize:11,fontWeight:600,
                  opacity:restarting?0.5:1,
                }}>↺ {restarting?"Restarting...":"Restart"}</button>
                <button onClick={()=>loadLogs(selCont)} style={{
                  background:theme.inputBg,border:`1px solid ${theme.border}`,
                  color:theme.textMuted,borderRadius:5,padding:"3px 8px",cursor:"pointer",
                  fontFamily:"'Inter',sans-serif",fontSize:11,fontWeight:600,
                }}>↻</button>
                <button onClick={()=>{setSelCont(null);setLogs(null);}} style={{
                  background:"transparent",border:"none",color:theme.textMuted,
                  cursor:"pointer",fontSize:16,padding:"0 2px"}}>✕</button>
              </div>
            </div>
            <div style={{overflowY:"auto",padding:12,
              background:theme.logBg,fontFamily:"'JetBrains Mono',monospace",fontSize:11,
              flex:"1 1 0",minHeight:0}}>
              {logsLoading && (
                <div style={{color:theme.textMuted,textAlign:"center",padding:20}}>Loading logs...</div>
              )}
              {logs && logs
                .filter(line => !errOnly || /error|exception|fatal|oom|killed|warn/i.test(line))
                .slice(-1000)
                .map((line,i)=>{
                  const isError = /error|exception|fatal|oom|killed|warn/i.test(line);
                  const isInfo  = /info/i.test(line);
                  return (
                    <div key={i} style={{
                      color: isError?"#ef4444":isInfo?"#64748b":theme.text,
                      padding:"1px 0",borderBottom:`1px solid ${theme.border}10`,
                      wordBreak:"break-all",lineHeight:1.5,
                      background:isError?"rgba(239,68,68,0.05)":"transparent",
                    }}>{line}</div>
                  );
                })}
            </div>
          </div>
        )}
      {showDockerLogs && (
        <div style={{margin:"0 16px 12px",background:theme.bgCard,
          border:"1px solid rgba(245,158,11,0.3)",borderRadius:10,padding:12}}>
          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
            <span style={{fontSize:12,fontWeight:700,color:"#f59e0b"}}>📋 Container Log Sizes</span>
            <button onClick={loadDockerLogs} style={{marginLeft:"auto",fontSize:11,
              padding:"2px 8px",borderRadius:4,cursor:"pointer",
              background:theme.inputBg,border:`1px solid ${theme.border}`,color:theme.textMuted}}>↻</button>
          </div>
          {logsLoading && <div style={{color:theme.textMuted,fontSize:12}}>Loading...</div>}
          {dockerLogs.map((l,i)=>(
            <div key={i} style={{display:"flex",alignItems:"center",gap:8,
              padding:"5px 8px",borderRadius:4,marginBottom:2,
              background:i%2===0?theme.inputBg:"transparent"}}>
              <span style={{color:l.size_bytes>1073741824?"#ef4444":l.size_bytes>536870912?"#f59e0b":"#10b981",
                fontWeight:700,fontFamily:"monospace",minWidth:80}}>{l.size}</span>
              <span style={{color:theme.text,fontSize:11,flex:1}}>{l.name}</span>
              <button onClick={()=>truncateDockerLog(l.name)} style={{
                fontSize:10,padding:"2px 8px",borderRadius:4,cursor:"pointer",
                background:"rgba(239,68,68,0.1)",border:"1px solid rgba(239,68,68,0.3)",
                color:"#ef4444",fontWeight:600}}>Truncate</button>
            </div>
          ))}
        </div>
      )}

      {showDockerHistory && (
        <div style={{margin:"0 16px 12px",background:theme.bgCard,
          border:"1px solid rgba(239,68,68,0.3)",borderRadius:10,padding:12}}>
          <div style={{fontSize:12,fontWeight:700,color:"#ef4444",marginBottom:8}}>
            🔄 Docker Auto-Restart History
          </div>
          {dockerHistory.length===0 ? (
            <div style={{fontSize:11,color:theme.textMuted,padding:8}}>No restarts recorded yet</div>
          ) : (
            <div>
              {dockerHistory.length>0 && console.log("First item keys:", Object.keys(dockerHistory[0]), "container:", dockerHistory[0].container)}
              {dockerHistory.map((h,idx)=>(
                <div key={idx} style={{padding:"6px 8px",fontSize:11,
                  background:idx%2===0?theme.inputBg:"transparent",
                  borderRadius:4,marginBottom:2}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                    <div>
                      <span style={{color:"#38bdf8",fontWeight:700,fontSize:12}}>{h.container}</span>
                      <span style={{color:"#64748b",fontSize:10,marginLeft:8}}>{h.time}</span>
                    </div>
                    <span style={{color:h.auto?"#a78bfa":"#10b981",fontSize:9,fontWeight:700,padding:"1px 6px",background:"rgba(0,0,0,0.3)",borderRadius:3}}>{h.auto?"AUTO":"MANUAL"}</span>
                  </div>
                  <div style={{color:"#f59e0b",fontSize:10,marginTop:2}}>{h.reason}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {showImages&&(<div style={{margin:"0 16px 12px",border:"1px solid #06b6d440",borderRadius:10,padding:12}}><div style={{display:"flex",gap:8,marginBottom:8,alignItems:"center"}}><span style={{fontSize:12,fontWeight:700,color:"#06b6d4"}}>🖼 Docker Images</span><span style={{fontSize:11,color:"#ef4444",marginLeft:8}}>{images.filter(x=>!x.in_use).length} unused</span><button onClick={pruneImages} style={{marginLeft:"auto",fontSize:11,padding:"2px 10px",borderRadius:4,cursor:"pointer",background:"rgba(239,68,68,0.1)",border:"1px solid #ef4444",color:"#ef4444"}}>Prune</button><button onClick={loadImages} style={{fontSize:11,padding:"2px 8px",borderRadius:4,cursor:"pointer",background:theme.inputBg,border:"1px solid #334155",color:"#94a3b8"}}>↻</button></div>{imgLoading&&<div style={{color:"#94a3b8",fontSize:12}}>Loading...</div>}<div style={{maxHeight:260,overflowY:"auto"}}>{images.map((img,idx)=>(<div key={idx} style={{display:"grid",gridTemplateColumns:"1fr 90px 60px 70px 60px",gap:6,padding:"5px 8px",borderRadius:4,marginBottom:2,background:idx%2===0?theme.inputBg:"transparent",alignItems:"center",fontSize:11}}><div><div style={{color:theme.text,fontWeight:600,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{img.repo}</div><div style={{fontSize:9,color:theme.textMuted}}>{img.id}</div></div><span style={{color:theme.textMuted,fontSize:10,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{img.tag}</span><span style={{color:"#06b6d4",fontWeight:700}}>{img.size}</span><span style={{color:theme.textMuted,fontSize:10}}>{img.created}</span>{img.in_use?<span style={{fontSize:9,color:"#10b981",fontWeight:700}}>IN USE</span>:<button onClick={()=>deleteImage(img)} style={{fontSize:10,padding:"2px 6px",borderRadius:3,cursor:"pointer",background:"rgba(239,68,68,0.1)",border:"1px solid #ef4444",color:"#ef4444"}}>Del</button>}</div>))}</div></div>)}
      </div>
    </div>
  );
}
